import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { ANNUAL_PROGRAMS_CHOOSE_CATEGORY_FRAGMENT_ID } from '@/_i18n/section-params'

export interface ILink {
	label: string
	to: string
	children?: ILink[]
	highlight?: boolean
	prefetch?: boolean
	bold?: boolean
	/** When true, active only on exact path match (hub listing vs /.../sub-routes). */
	exactActive?: boolean
	/** Prefix match excludes URLs whose first segment after this link path is listed (e.g. sibling landing page under same hub). */
	activeExcludeNextSegments?: string[]
}

export const useHeaderLinks = () => {
	const t = useTranslations('layout.header')

	const mainLinks: ILink[] = useMemo(
		() => [
			{
				label: t('links.discounts.label'),
				to: '/akciova-ponuka',
				prefetch: true
			},
			{
				label: t('links.team.label'),
				to: '/tim',
				prefetch: true
			},
			{
				label: t('links.blog.label'),
				to: '/clanky',
				prefetch: true
			},
			{
				label: t('links.forCompanies.label'),
				to: '/pre-firmy',
				prefetch: true
			},
			{
				label: t('links.career.label'),
				to: '/kariera',
				prefetch: true,
				bold: true,
				children: [
					{
						label: t('links.career.children.openPositions'),
						to: '/kariera',
						prefetch: true,
						activeExcludeNextSegments: [
							'podte-robit-svet-zdravsim',
							'lets-make-world-healthier'
						]
					},
					{
						label: t('links.career.children.makeWorldHealthier'),
						to: '/kariera/podte-robit-svet-zdravsim',
						prefetch: true
					}
				]
			},
			{
				label: t('links.contact.label'),
				to: '/kontakt'
			}
		],
		[t]
	)

	const secondaryLinks: ILink[] = useMemo(
		() => [
			{
				label: t('links.ambulances.label'),
				to: '/ambulancie',
				prefetch: true
			},
			{
				label: t('links.annualPrograms.label'),
				to: '/rocne-programy',
				prefetch: true,
				children: [
					{
						label: 'Všetky ročné programy',
						to: `/rocne-programy#${ANNUAL_PROGRAMS_CHOOSE_CATEGORY_FRAGMENT_ID}`,
						prefetch: true,
						exactActive: true
					},
					{
						label: 'Pre ženy',
						to: '/rocne-programy#rocny-program-zeny',
						children: [
							{
								label: 'Medante Balance',
								to: '/rocne-programy/medante-balance',
								prefetch: true
							},
							{
								label: 'Medante Vital',
								to: '/rocne-programy/medante-vital',
								prefetch: true
							},
							{
								label: 'Medante Platinum',
								to: '/rocne-programy/medante-platinum',
								prefetch: true
							},
							{
								label: 'Medante Solo Gynekológ',
								to: '/rocne-programy/medante-solo-gynekolog',
								prefetch: true
							}
						]
					},
					{
						label: 'Pre mužov',
						to: '/rocne-programy#rocny-program-muzi',
						children: [
							{
								label: 'Medante Balance',
								to: '/rocne-programy/medante-balance',
								prefetch: true
							},
							{
								label: 'Medante Vital',
								to: '/rocne-programy/medante-vital',
								prefetch: true
							},
							{
								label: 'Medante Platinum',
								to: '/rocne-programy/medante-platinum',
								prefetch: true
							}
						]
					},
					{
						label: 'Pre deti',
						to: '/rocne-programy#rocny-program-dieta',
						children: [
							{
								label: 'Medante Solo Pediater',
								to: '/rocne-programy/medante-solo-pediater',
								prefetch: true
							},
							{
								label: 'Medante Comfort Junior',
								to: '/rocne-programy/medante-comfort-junior',
								prefetch: true
							}
						]
					}
				]
			},
			{
				label: t('links.preventiveExaminations.label'),
				to: '/preventivne-prehliadky',
				prefetch: true,
				children: [
					{
						label: 'Všetky prehliadky',
						to: '/preventivne-prehliadky',
						prefetch: true,
						exactActive: true
					},
					{
						label: 'Pre ženy',
						to: '/preventivne-prehliadky/zeny',
						prefetch: true
					},
					{
						label: 'Pre mužov',
						to: '/preventivne-prehliadky/muzi',
						prefetch: true
					}
				]
			},
			{
				label: t('links.medanteCredit.label'),
				to: '/medante-kredit',
				prefetch: true
			},
			{
				label: t('links.vipCare.label'),
				to: '/rocne-programy/medante-platinum',
				prefetch: true
			}
		],
		[t]
	)

	return {
		mainLinks,
		secondaryLinks
	}
}
